/**
 * Catalogo, disponibilidade e busca na superficie **PUBLICA** — 6 das 7 rotas de
 * `CONTRACTS §8.2` bloco "Catalogo e busca":
 *
 * `GET /v1/catalog/categories` · `GET /v1/catalog/products` · `GET /v1/catalog/products/{slug}`
 * `GET /v1/catalog/availability` · `GET /v1/search` · `GET /v1/search/suggest`
 *
 * A setima, `GET /v1/home`, esta em `home.ts` — ela reusa `ProductListItemSchema` daqui e por
 * isso herda os dois gates estruturais descritos abaixo. Ate o commit b0e3e88 este cabecalho
 * dizia que ela "e do modulo `cms`"; nao e: o modulo `cms` nunca existiu, e a Home do MVP e
 * composta pelo `catalog`, que ja tem o gate regulatorio de saida e a cascata de preco.
 *
 * Fontes: `API §6.6` e `§6.7` (o que cada rota devolve), `§6.20.2` (ficha) e `§6.20.3` (busca),
 * `§1.4`–`§1.8` (cursor, ordenacao, filtro, dinheiro), `§8.1` (cache),
 * `CONTRACTS §2.6` (classes), `§2.13` (faixa) e `§3.1` (a cascata).
 *
 * **Tres coisas que este arquivo DECIDE, e nao apenas descreve:**
 *
 * **1. `onHand` nao existe neste arquivo, e isso e verificavel.** RN-063 / X-22: o cliente
 * nunca ve numero absoluto de estoque. O unico numero de quantidade que sai e
 * `maxOrderableQty`, que ja e o saldo **ofertavel** (`onHand − safetyBuffer − reservedQty`) e
 * nao revela o saldo real. `catalog.test.ts` varre as chaves de todo schema deste arquivo e
 * reprova se `onHand`, `onHandQty`, `stock`, `reservedQty` ou `safetyBuffer` aparecerem.
 *
 * **2. A classe regulatoria do wire e MENOR que o enum do produto.**
 * `PUBLIC_REGULATORY_CLASS_VALUES` exclui `CTRL_*` **e** `OUTRO`. `API §6.20.2` escreve
 * `Exclude<RegulatoryClass, 'CTRL_${string}'>`; a exclusao de `OUTRO` e adicional e vem de
 * `CONTRACTS §2.6` ("participa de zero promocoes e **nao aparece em vitrine** ate ser
 * reclassificado"). O efeito pratico e que o gate regulatorio deixa de ser so codigo: se o
 * repositorio um dia vazar um controlado, `ProductDetailSchema.parse` da propria resposta
 * **lanca** antes de o corpo sair — o contrato e a ultima linha de defesa, e ela e automatica.
 *
 * **3. O `Cache-Control` de cada rota e CONTRATO, nao detalhe do controller.** Ele mora aqui
 * como constante porque tres consumidores dependem do valor exato: a borda, a allowlist do
 * service worker (PWA §4.2 R10) e o teste de integracao, que o compara literalmente. Uma
 * segunda grafia num controller e como a divergencia comeca.
 */
import { CONTROLLED_CLASSES, REGULATORY_CLASSES } from '@farmacia/domain';
import { z } from 'zod';

import { AvailabilityBandSchema } from '../enums';
import {
  CursorSchema,
  InstantSchema,
  MoneySchema,
  NonNegativeMoneySchema,
  PAGE_LIMIT_DEFAULT,
  PAGE_LIMIT_MAX_CATALOG,
  SlugSchema,
  Uuidv7Schema,
  paginated,
} from '../common';

// ---------------------------------------------------------------------------
// Classe regulatoria publicavel — o subconjunto que pode atravessar o wire
// ---------------------------------------------------------------------------

const CONTROLADAS: ReadonlySet<string> = new Set<string>(CONTROLLED_CLASSES);

/**
 * As classes que podem aparecer numa resposta `PUB`/`SES`.
 *
 * Derivada, nunca redigitada: some `CTRL_*` (REG-002 — invisiveis em toda instancia, sem flag
 * e sem gate) e some `OUTRO` (CONTRACTS §2.6 — fail-closed, nao aparece em vitrine ate ser
 * reclassificado). Se `CONTRACTS §2.6` ganhar uma 17a classe controlada, ela sai daqui sozinha.
 *
 * Note que **`RX_SIMPLES`/`RX_RETENCAO` continuam na lista**: a decisao de exibi-las e do
 * perfil (`regulatory.rxVisibility`, default `OCULTA`) e do gate de quatro provas, nao do
 * formato do wire. Tirar a classe do schema impediria a instancia que TEM o gate de responder.
 */
export const PUBLIC_REGULATORY_CLASS_VALUES = REGULATORY_CLASSES.filter(
  (classe) => !CONTROLADAS.has(classe) && classe !== 'OUTRO',
) as readonly Exclude<
  (typeof REGULATORY_CLASSES)[number],
  `CTRL_${string}` | 'OUTRO'
>[] as readonly [
  Exclude<(typeof REGULATORY_CLASSES)[number], `CTRL_${string}` | 'OUTRO'>,
  ...Exclude<(typeof REGULATORY_CLASSES)[number], `CTRL_${string}` | 'OUTRO'>[],
];

export const PublicRegulatoryClassSchema = z.enum(PUBLIC_REGULATORY_CLASS_VALUES);
export type PublicRegulatoryClass = z.infer<typeof PublicRegulatoryClassSchema>;

// ---------------------------------------------------------------------------
// Categorias — `GET /v1/catalog/categories`
// ---------------------------------------------------------------------------

/**
 * Um no de categoria (~40 nos no total, `API §6.6`).
 *
 * **A arvore viaja PLANA, ordenada, com `path` e `parentId` — e nao aninhada.** `API §6.6` diz
 * "arvore", e ela continua sendo uma arvore: `path` e o *materialized path* que
 * `DATA_MODEL §3.4` escolheu exatamente para que a hierarquia seja derivavel em uma passada
 * (`"saude/medicamentos/analgesicos"`). A decisao de nao aninhar tem duas razoes, e a primeira e
 * dura:
 *
 * **1. Um schema recursivo nao e documentavel nesta topologia.** `children: CategoryNode[]`
 * exige `z.lazy`, e `zod-to-openapi` so descreve `z.lazy` com metadata `.openapi({...})` no
 * proprio schema. Chamar `.openapi()` aqui exigiria que `packages/contracts` dependesse de
 * `@asteasolutions/zod-to-openapi` — e o gate `contracts-so-depende-de-domain` reprova
 * (`.dependency-cruiser.cjs`), com razao: o contrato descreve a API, nao a ferramenta que a
 * documenta. As saidas seriam redigitar a forma do no em OpenAPI cru (a duplicacao que
 * `API §10.3` proibe) ou fixar uma profundidade maxima inventada. Nenhuma das duas e melhor que
 * transportar o `path` que ja existe.
 *
 * **2. Plana e menor e mais facil de renderizar.** Ordenada por `(depth, position)`, a lista da
 * um `for` de uma passada para montar a arvore, e nao ha caminho em que um no chegue antes do
 * pai. O payload perde o aninhamento e nao perde informacao nenhuma.
 *
 * `productCount` **nao** existe: contagem por categoria muda a cada sync de 15 min e
 * invalidaria uma resposta cacheada por 10 min na borda por um numero decorativo.
 */
export const CategoryNodeSchema = z.object({
  id: Uuidv7Schema,
  /** `null` na raiz. Com `path`, e o que permite montar a arvore em uma passada. */
  parentId: Uuidv7Schema.nullable(),
  slug: SlugSchema,
  name: z.string().min(1),
  /** Materialized path (`saude/medicamentos/analgesicos`) — DATA_MODEL §3.4. */
  path: z.string().min(1),
  depth: z.number().int().min(0),
  position: z.number().int().min(0),
  iconKey: z.string().min(1).nullable(),
  /** REG-044: categoria terapeutica nao pode ser usada em segmentacao de marketing. */
  isTherapeutic: z.boolean(),
});
export type CategoryNode = z.infer<typeof CategoryNodeSchema>;

/**
 * A arvore inteira, **sem envelope de pagina**.
 *
 * `API §1.4` manda toda colecao paginar por cursor; esta e a mesma excecao ja registrada em
 * `stores.ts`: a colecao e fechada (~40 nos, `API §6.6`) e uma arvore paginada nao e uma
 * arvore — a pagina 2 chegaria com nos cujo pai ficou na pagina 1. Truncar seria pior.
 *
 * Ordem garantida: `(depth ASC, position ASC, slug ASC)`. Ela e contrato, e nao detalhe: o
 * cliente monta a arvore assumindo que o pai vem antes do filho, e o `ETag` de uma resposta
 * cacheada por 10 min e calculado sobre o corpo — ordem instavel geraria `ETag` novo a cada
 * requisicao para um conteudo identico.
 */
export const CategoryTreeResponseSchema = z.object({
  data: z.array(CategoryNodeSchema),
});
export type CategoryTreeResponse = z.infer<typeof CategoryTreeResponseSchema>;

// ---------------------------------------------------------------------------
// Preco exibido — `ChargedPrice` / `InformativePrice` (API §6.20.2, CONTRACTS §3.4)
// ---------------------------------------------------------------------------

/**
 * Union que torna **impossivel** renderizar preco nao garantido sem disclaimer (REG-081).
 *
 * `guaranteed: true` e literal, nao booleano: um `InformativePrice` nao consegue se passar por
 * `ChargedPrice` nem por acidente de spread.
 */
export const ChargedPriceSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('REGULAR'), amount: NonNegativeMoneySchema, guaranteed: z.literal(true) }),
  z.object({
    kind: z.literal('PROMOCIONAL'),
    amount: NonNegativeMoneySchema,
    guaranteed: z.literal(true),
    promotionId: Uuidv7Schema,
  }),
  z.object({
    kind: z.literal('CLUBE'),
    amount: NonNegativeMoneySchema,
    guaranteed: z.literal(true),
    tier: z.string().min(1),
  }),
]);
export type ChargedPrice = z.infer<typeof ChargedPriceSchema>;

/** REG-083: preco informativo (PBM/PFPB) **nunca** compoe total. O `disclaimer` e obrigatorio. */
export const InformativePriceSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('INFORMATIVO_PFPB'),
    amount: MoneySchema.nullable(),
    guaranteed: z.literal(false),
    disclaimer: z.string().min(1),
  }),
  z.object({
    kind: z.literal('INFORMATIVO_PBM'),
    amount: MoneySchema.nullable(),
    guaranteed: z.literal(false),
    disclaimer: z.string().min(1),
  }),
]);
export type InformativePrice = z.infer<typeof InformativePriceSchema>;

// ---------------------------------------------------------------------------
// Disponibilidade — `StoreAvailability` (CONTRACTS §2.13, DOMAIN §4.8)
// ---------------------------------------------------------------------------

/**
 * O bloco de disponibilidade, e **tudo** o que o cliente sabe sobre estoque.
 *
 * O que NAO esta aqui, por RN-063: `onHand`, `reservedQty`, `safetyBuffer`. `maxOrderableQty`
 * fica porque o carrinho precisa de um teto para o seletor de quantidade, e ele ja e o saldo
 * ofertavel — nao o saldo real.
 *
 * `isStale` e derivado na LEITURA, contra o relogio da requisicao: `erpSyncedAt` mais velho que
 * `integration.staleness.inventorySec` (default 2700 s = 45 min) degrada a filial inteira para
 * `ON_REQUEST` (RN-066). Nao da para congelar isso numa coluna, e por isso ele viaja.
 */
export const StoreAvailabilitySchema = z.object({
  storeId: Uuidv7Schema,
  sku: z.string().min(1),
  band: AvailabilityBandSchema,
  /** `max(0, onHand − safetyBuffer − reservedQty)` (RN-064). Teto do seletor de quantidade. */
  maxOrderableQty: z.number().int().min(0),
  syncedAt: InstantSchema,
  /** RN-066 — `syncedAt` alem do orcamento de frescor do perfil. */
  isStale: z.boolean(),
});
export type StoreAvailability = z.infer<typeof StoreAvailabilitySchema>;

/** RN-074 — "estoque nunca e promessa". Texto normativo, exibido em toda tela de faixa. */
export const AVAILABILITY_DISCLAIMER_PT_BR =
  'Disponibilidade sujeita à confirmação da loja.' as const;

// ---------------------------------------------------------------------------
// Produto — item de listagem e ficha
// ---------------------------------------------------------------------------

export const ProductImageSchema = z.object({
  url: z.string().min(1),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  /** Nunca vazio: imagem sem `alt` reprova o gate de acessibilidade do DESIGN_SYSTEM. */
  alt: z.string().min(1),
});
export type ProductImage = z.infer<typeof ProductImageSchema>;

/**
 * O bloco regulatorio da resposta — o que a UI precisa para **nao** cometer infracao.
 *
 * `class` usa `PublicRegulatoryClassSchema`, e nao `RegulatoryClassSchema`: ver a decisao 2 no
 * topo do arquivo. Um `CTRL_*` que chegasse aqui derruba o `parse` da propria resposta.
 */
export const ProductRegulatorySchema = z.object({
  class: PublicRegulatoryClassSchema,
  requiresPrescription: z.boolean(),
  requiresRetention: z.boolean(),
  /** REG-035 — advertencia obrigatoria de MIP, renderizada com contraste >= 4.5:1. */
  mandatoryWarning: z.string().min(1).nullable(),
  canBePromoted: z.boolean(),
  canBeCrossSold: z.boolean(),
  /** REG-013 — teto por SKU no pedido. `null` = sem teto sanitario. */
  maxQuantityPerOrder: z.number().int().min(0).nullable(),
  /** "Venda sob prescricao medica" — texto e norma, para transparencia e fiscalizacao. */
  saleNotice: z.string().min(1).nullable(),
});
export type ProductRegulatory = z.infer<typeof ProductRegulatorySchema>;

/**
 * Item de listagem e de busca (`ProductListItem` de `API §6.20.3`).
 *
 * E deliberadamente menor que a ficha: `descriptionHtml`, `leafletUrl` e `informative` ficam de
 * fora porque uma pagina de 24 produtos com bula e descricao sanitizada custa ~38 KB contra
 * ~9 KB (o numero de `API §1.7`), e em 3G isso e a diferenca entre listar e desistir.
 */
export const ProductListItemSchema = z.object({
  id: Uuidv7Schema,
  slug: SlugSchema,
  /** Nome de vitrine CURADO. **Nunca** o `rawName` do ERP ("DIP SOD 500MG C/10 CPR GEN EMS"). */
  displayName: z.string().min(1),
  brand: z.string().min(1).nullable(),
  manufacturer: z.string().min(1).nullable(),
  presentation: z.string().min(1).nullable(),
  dosage: z.string().min(1).nullable(),
  /** `null` quando o EAN do ERP falhou no digito verificador (`gtinSuspect`). */
  gtin: z.string().regex(/^\d{8,14}$/).nullable(),
  image: ProductImageSchema.nullable(),
  regulatory: ProductRegulatorySchema,
  charged: ChargedPriceSchema,
  /** REG-033 — so existe com `PriceHistory` provando o preco anterior. Sem prova, `null`. */
  compareAt: NonNegativeMoneySchema.nullable(),
  availability: StoreAvailabilitySchema,
});
export type ProductListItem = z.infer<typeof ProductListItemSchema>;

/** `GET /v1/catalog/products` — `{ data, page }` de `API §1.1`, com o cursor de `§1.4`. */
export const ProductListResponseSchema = paginated(ProductListItemSchema);
export type ProductListResponse = z.infer<typeof ProductListResponseSchema>;

/** A ficha completa — `API §6.20.2`. */
export const ProductDetailSchema = ProductListItemSchema.extend({
  activeIngredient: z.string().min(1).nullable(),
  images: z.array(ProductImageSchema),
  /** Sanitizado no SERVIDOR. O cliente nunca sanitiza HTML que o servidor mandou cru. */
  descriptionHtml: z.string().nullable(),
  /** REG-014 — bula do paciente. */
  leafletUrl: z.string().min(1).nullable(),
  /** REG-083 — exibido, **nunca** compoe total. */
  informative: z.array(InformativePriceSchema),
  /** "R$ 1,29 / comprimido". `null` quando a apresentacao nao permite derivar. */
  unitPrice: z.object({ amount: NonNegativeMoneySchema, unit: z.string().min(1) }).nullable(),
  /** Teto CMED da UF — exposto para auditoria e para o painel do RT. `null` em nao medicamento. */
  cmedPmc: NonNegativeMoneySchema.nullable(),
  storeId: Uuidv7Schema,
  /** REG-080 — informativo. */
  farmaciaPopular: z.object({ eligible: z.boolean(), note: z.string().min(1).nullable() }).nullable(),
});
export type ProductDetail = z.infer<typeof ProductDetailSchema>;

// ---------------------------------------------------------------------------
// Query de listagem — filtros (API §1.6), ordenacao (§1.5) e cursor (§1.4)
// ---------------------------------------------------------------------------

/**
 * Whitelist de ordenacao de `catalog/products` (`API §1.5`). Campo fora da lista e
 * `400 INVALID_SORT_FIELD` — nunca "ordena por outra coisa e nao avisa".
 */
export const CATALOG_SORT_FIELDS = ['relevance', 'price', 'name', 'discount'] as const;
export type CatalogSortField = (typeof CATALOG_SORT_FIELDS)[number];

/** A mesma whitelist, ja na grafia do wire (`-campo` = descendente, `API §1.5`). */
export const CATALOG_SORT_VALUES = [
  'relevance',
  '-relevance',
  'price',
  '-price',
  'name',
  '-name',
  'discount',
  '-discount',
] as const;

export const CatalogSortSchema = z.enum(CATALOG_SORT_VALUES).default('-relevance');

/**
 * `?fields=` de `API §1.7` — sparse fieldset, suportado em **apenas dois recursos**, e este e
 * um deles. Campo fora da whitelist e **ignorado em silencio** (nao e erro: um cliente antigo
 * pedindo campo removido nao pode quebrar).
 */
export const CATALOG_SPARSE_FIELDS = [
  'id',
  'slug',
  'displayName',
  'brand',
  'manufacturer',
  'presentation',
  'dosage',
  'gtin',
  'image',
  'regulatory',
  'charged',
  'compareAt',
  'availability',
] as const;
export type CatalogSparseField = (typeof CATALOG_SPARSE_FIELDS)[number];

/**
 * Os campos que `?fields=` **nunca** remove.
 *
 * `API §1.7`: "campo sensivel nunca e liberado por `fields` — a autorizacao nao olha para esse
 * parametro". Aqui a leitura e a simetrica: `regulatory` e `availability` nao podem ser
 * SUPRIMIDOS, porque um item sem `regulatory` e um item que a UI renderiza sem a advertencia
 * obrigatoria de MIP (REG-035), e um item sem `availability` e um item que ela renderiza como
 * compravel sem saber se ha saldo. Economia de bytes nao compra infracao sanitaria.
 */
export const CATALOG_MANDATORY_FIELDS = ['id', 'slug', 'displayName', 'regulatory', 'availability'] as const;

const LimitSchema = z.coerce.number().int().min(1).max(PAGE_LIMIT_MAX_CATALOG).default(PAGE_LIMIT_DEFAULT);

/** `availability=IN_STOCK` filtra; `ANY` nao filtra (API §6.20.3). */
export const AvailabilityFilterSchema = z.enum(['ANY', 'IN_STOCK']).default('ANY');

export const ProductListQuerySchema = z.object({
  /**
   * **Obrigatorio** (`API §6.6`): preco e disponibilidade dependem da filial, e uma listagem
   * sem `storeId` so poderia devolver preco de ninguem. Ausente ⇒ `422 VALIDATION_ERROR`.
   */
  storeId: Uuidv7Schema,
  categorySlug: SlugSchema.optional(),
  brandSlug: SlugSchema.optional(),
  priceMinCents: z.coerce.number().int().min(0).optional(),
  priceMaxCents: z.coerce.number().int().min(0).optional(),
  availability: AvailabilityFilterSchema,
  hasImage: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
  sort: CatalogSortSchema,
  limit: LimitSchema,
  cursor: CursorSchema.optional(),
  /** Lista separada por virgula; campo desconhecido e descartado sem erro (API §1.7). */
  fields: z.string().min(1).optional(),
});
export type ProductListQuery = z.infer<typeof ProductListQuerySchema>;

/** Parametro de caminho de `GET /v1/catalog/products/{slug}`. */
export const ProductSlugParamSchema = z.object({ slug: SlugSchema });
export type ProductSlugParam = z.infer<typeof ProductSlugParamSchema>;

// ---------------------------------------------------------------------------
// `GET /v1/catalog/availability`
// ---------------------------------------------------------------------------

/** `API §6.6`: `?storeId=&skus=a,b,c`, **ate 50**. */
export const AVAILABILITY_MAX_SKUS = 50 as const;

export const AvailabilityQuerySchema = z.object({
  storeId: Uuidv7Schema,
  /**
   * Lista separada por virgula. O teto de 50 e do contrato, nao do banco: sem ele, um cliente
   * pediria o catalogo inteiro numa rota `no-store` e transformaria a consulta mais quente do
   * sistema numa varredura.
   */
  skus: z
    .string()
    .min(1)
    .transform((raw) => raw.split(',').map((s) => s.trim()).filter((s) => s !== ''))
    .pipe(z.array(z.string().min(1)).min(1).max(AVAILABILITY_MAX_SKUS)),
});
export type AvailabilityQuery = z.infer<typeof AvailabilityQuerySchema>;

/**
 * Resposta de `GET /v1/catalog/availability`.
 *
 * SKU que nao existe, nao esta publicado ou e de classe nao vendavel **simplesmente nao volta**
 * na lista — nao ha entrada com `band: null` nem erro por item. Distinguir "nao existe" de
 * "existe mas voce nao pode ver" e o mesmo vazamento que REG-051 fecha em `404 vs 403`, so que
 * em lote: um atacante mandaria 50 SKUs por requisicao e leria a diferenca.
 */
export const AvailabilityResponseSchema = z.object({
  data: z.array(StoreAvailabilitySchema),
  /** RN-074 — sempre presente, para nenhuma superficie exibir faixa sem a ressalva. */
  disclaimer: z.literal(AVAILABILITY_DISCLAIMER_PT_BR),
});
export type AvailabilityResponse = z.infer<typeof AvailabilityResponseSchema>;

// ---------------------------------------------------------------------------
// `GET /v1/search` e `GET /v1/search/suggest` — API §6.20.3
// ---------------------------------------------------------------------------

export const SEARCH_MIN_QUERY_LENGTH = 2 as const;
export const SEARCH_MAX_QUERY_LENGTH = 80 as const;
export const SUGGEST_MAX_RESULTS = 8 as const;

export const SearchTermSchema = z
  .string()
  .trim()
  .min(SEARCH_MIN_QUERY_LENGTH)
  .max(SEARCH_MAX_QUERY_LENGTH);

/** `API §6.20.3` — `sort` da busca e MENOR que o de `catalog/products`: sem `discount`. */
export const SEARCH_SORT_VALUES = ['relevance', 'price', '-price', 'name'] as const;

export const SearchQuerySchema = z.object({
  q: SearchTermSchema,
  storeId: Uuidv7Schema,
  scope: z.enum(['ALL', 'PRODUCTS', 'CATEGORIES']).default('ALL'),
  categorySlug: SlugSchema.optional(),
  availability: AvailabilityFilterSchema,
  sort: z.enum(SEARCH_SORT_VALUES).default('relevance'),
  limit: LimitSchema,
  cursor: CursorSchema.optional(),
});
export type SearchQuery = z.infer<typeof SearchQuerySchema>;

/**
 * `meta` da busca (`API §6.20.3`).
 *
 * `totalApprox` e **estimativa declarada**, nunca `COUNT(*)`: contar exato num catalogo de
 * dezenas de milhares de linhas custa uma varredura por consulta para entregar um numero que
 * ninguem usa para decidir nada.
 */
export const SearchMetaSchema = z.object({
  totalApprox: z.number().int().min(0),
  /** Termo corrigido por `pg_trgm` ("dipironna" → "dipirona"). `null` quando nao houve. */
  correctedFrom: z.string().min(1).nullable(),
  didYouMean: z.array(z.string().min(1)),
  activeIngredientMatch: z.string().min(1).nullable(),
  genericsAvailable: z.boolean(),
  /** Entrou na fila do curador (`search_term_stats` com `resultCount = 0`). */
  zeroResultLogged: z.boolean(),
});
export type SearchMeta = z.infer<typeof SearchMetaSchema>;

export const SearchResponseSchema = paginated(ProductListItemSchema).extend({
  meta: SearchMetaSchema,
});
export type SearchResponse = z.infer<typeof SearchResponseSchema>;

export const SuggestQuerySchema = z.object({
  q: SearchTermSchema,
  storeId: Uuidv7Schema,
  limit: z.coerce.number().int().min(1).max(SUGGEST_MAX_RESULTS).default(SUGGEST_MAX_RESULTS),
});
export type SuggestQuery = z.infer<typeof SuggestQuerySchema>;

/**
 * Uma sugestao de autocomplete.
 *
 * **Sem preco e sem disponibilidade, de proposito.** O `suggest` responde em < 120 ms (`API
 * §6.7`) porque toca uma consulta de prefixo e mais nada; juntar preco por filial faria cada
 * tecla digitada custar o JOIN da listagem. `kind` diz ao cliente para onde navegar.
 */
export const SuggestionSchema = z.object({
  kind: z.enum(['PRODUTO', 'CATEGORIA', 'PRINCIPIO_ATIVO']),
  /** O texto a exibir e a completar na caixa de busca. */
  text: z.string().min(1),
  /** Alvo direto quando `kind` e `PRODUTO` ou `CATEGORIA`; `null` em principio ativo. */
  slug: SlugSchema.nullable(),
});
export type Suggestion = z.infer<typeof SuggestionSchema>;

export const SuggestResponseSchema = z.object({
  data: z.array(SuggestionSchema).max(SUGGEST_MAX_RESULTS),
});
export type SuggestResponse = z.infer<typeof SuggestResponseSchema>;

// ---------------------------------------------------------------------------
// Cache — API §8.1, uma constante por rota
// ---------------------------------------------------------------------------

/** `GET /v1/catalog/categories` — muda raramente; e da allowlist do service worker (PWA §4.2 R10). */
export const CATALOG_CATEGORIES_CACHE_CONTROL = 'public, s-maxage=600, stale-while-revalidate=86400';

/** `GET /v1/catalog/products` e `/{slug}`, **anonimo**. Chave inclui `storeId`, filtros e `fields`. */
export const CATALOG_PRODUCTS_CACHE_CONTROL = 'public, s-maxage=60, stale-while-revalidate=300';

/**
 * Catalogo **com beneficio de clube na resposta** — `API §8.1`: "preco personalizado em CDN e
 * vazamento e preco errado para o proximo visitante".
 */
export const CATALOG_PERSONALIZED_CACHE_CONTROL = 'private, no-store';

/** `GET /v1/catalog/availability` — "estoque nunca e cacheado. Nem 5 s" (`API §8.1`). */
export const CATALOG_AVAILABILITY_CACHE_CONTROL = 'no-store';

/**
 * `GET /v1/search` e `GET /v1/search/suggest` — `private, no-store` **sempre** (`API §8.1`,
 * §6.7): termo de busca de medicamento e potencialmente dado de saude, e nao pode ficar em CDN
 * nem no service worker.
 *
 * DUVIDA (reportada): a pauta desta rodada descreve o `suggest` como "curto e **cacheavel**".
 * `API §6.7` e `§8.1` dizem `no-store` nas duas rotas, e API precede a pauta. Mantido
 * `no-store`; se o produto quiser cache de borda no `suggest`, isso e uma mudanca em `API §8.1`
 * ANTES do codigo — e exige separar sugestao de termo (cacheavel) de sugestao que revela
 * catalogo de medicamento (nao).
 */
export const SEARCH_CACHE_CONTROL = 'private, no-store';
